// 2026-09-23 (user request) — Register Buku Bank + Estatement + Breakdown.
// Kontainer 3 sub-sub-tab di dalam sub-tab "Input & Categorize":
// 1) Register (buku besar lama) · 2) Estatement (import CSV) · 3) Breakdown.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Landmark, Printer, BookOpen, FileUp, Tag, FileDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatIDR, fmtDate } from "@/lib/constants";
import { useAccess } from "@/lib/access";
import { useAuth } from "@/lib/auth";
import EstatementTab from "./EstatementTab";
import BreakdownTab from "./BreakdownTab";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const CONTAINER_TABS = [
  { key: "register", label: "Register Buku Bank", icon: BookOpen },
  { key: "estatement", label: "Estatement", icon: FileUp },
  { key: "breakdown", label: "Breakdown", icon: Tag },
];

export default function BankRegisterTab() {
  const [tab, setTab] = useState("register");
  const { canPerm } = useAccess();
  const { user } = useAuth();
  const canEstWrite = canPerm(user, "kas_bank_estatement", "create");
  const canEstDelete = canPerm(user, "kas_bank_estatement", "delete");
  const canClsWrite = canPerm(user, "kas_bank_classification", "create");
  const canClsDelete = canPerm(user, "kas_bank_classification", "delete");
  return (
    <div data-testid="bank-reg-container">
      <div className="flex items-center gap-1 flex-wrap mb-2" data-testid="ic-tabs">
        {CONTAINER_TABS.map((t) => (
          <button key={t.key} type="button" data-testid={`ic-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] uppercase tracking-wider font-semibold transition-colors ${tab === t.key ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0B1D3A] hover:text-[#0B1D3A]"}`}>
            <t.icon size={11} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "register" && <RegisterBookTab />}
      {tab === "estatement" && <EstatementTab canWrite={canEstWrite} canDelete={canEstDelete} />}
      {tab === "breakdown" && <BreakdownTab canWrite={canEstWrite} canClsWrite={canClsWrite} canClsDelete={canClsDelete} />}
    </div>
  );
}

function RegisterBookTab() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // 2026-09-30 (user request): standarisasi pola filter (seperti tab Report
  // Petty Cash) — pencarian transaksi + filter proyek + Export Excel.
  const [regSearch, setRegSearch] = useState("");
  const [regProject, setRegProject] = useState("");
  const [exporting, setExporting] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [loadingAcc, setLoadingAcc] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load daftar rekening dari ringkasan history bank.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bank-history");
        const list = Array.isArray(data) ? data : [];
        setAccounts(list);
        if (list.length > 0) setAccountId(list[0].id);
      } catch (e) {
        setError(e.response?.data?.detail || "Gagal memuat daftar rekening");
      } finally { setLoadingAcc(false); }
    })();
  }, []);

  // Load ledger tiap ganti rekening.
  useEffect(() => {
    if (!accountId) return;
    (async () => {
      setLoading(true); setError(null);
      try {
        const { data } = await api.get(`/bank-history/${accountId}/ledger`);
        setLedger(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Gagal memuat register");
        setLedger(null);
      } finally { setLoading(false); }
    })();
  }, [accountId]);

  // Opsi proyek untuk filter — dari nama proyek pada mutasi rekening ini.
  const projectOptions = useMemo(() => {
    const names = (ledger?.transactions || []).map((t) => t.project_name).filter(Boolean);
    return [...new Set(names)].sort();
  }, [ledger]);

  // Filter periode + hitung ulang saldo berjalan (saldo awal periode =
  // saldo akhir semua mutasi SEBELUM dateFrom).
  const view = useMemo(() => {
    if (!ledger) return null;
    const all = ledger.transactions || [];
    const from = dateFrom || "";
    const to = dateTo || "";
    const day = (t) => (t.date || "").slice(0, 10);
    let running = Number(ledger.opening_balance || 0);
    const rows = [];
    for (const t of all) {
      const d = day(t);
      if (from && d < from) { running = Number(t.running_balance ?? running); continue; }
      if (to && d > to) break;
      rows.push(t);
      running = Number(t.running_balance ?? running);
    }
    // Saldo awal periode = running balance mutasi terakhir SEBELUM from (atau opening).
    let openingPeriod = Number(ledger.opening_balance || 0);
    if (from) {
      for (const t of all) {
        if (day(t) < from) openingPeriod = Number(t.running_balance ?? openingPeriod);
        else break;
      }
    }
    // Filter proyek + pencarian hanya memfilter baris yang ditampilkan — saldo
    // berjalan & saldo akhir tetap mengikuti rentang tanggal.
    const kw = regSearch.trim().toLowerCase();
    const disp = rows.filter((t) => {
      if (regProject && (t.project_name || "") !== regProject) return false;
      if (!kw) return true;
      return [t.description, t.project_name, t.reference_no, t.classification]
        .some((v) => String(v || "").toLowerCase().includes(kw));
    });
    const totalIn = disp.filter((r) => r.direction === "in").reduce((a, r) => a + (Number(r.amount) || 0), 0);
    const totalOut = disp.filter((r) => r.direction === "out").reduce((a, r) => a + (Number(r.amount) || 0), 0);
    return {
      rows: disp, openingPeriod,
      closing: rows.length > 0 ? Number(rows[rows.length - 1].running_balance) : openingPeriod,
      totalIn, totalOut, count: disp.length,
    };
  }, [ledger, dateFrom, dateTo, regSearch, regProject]);

  const acc = ledger?.account || accounts.find((a) => a.id === accountId) || {};

  // Export Excel ledger — mengikuti rentang tanggal aktif (backend filter).
  const exportExcel = async () => {
    try {
      setExporting(true);
      const qs = new URLSearchParams();
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      const res = await api.get(`/bank-history/${accountId}/ledger/export.xlsx?${qs.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `register_buku_bank_${acc.bank_name || "rekening"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export Excel diunduh.");
    } catch {
      toast.error("Gagal export Excel.");
    } finally { setExporting(false); }
  };

  const printPdf = () => {
    if (!view) return;
    const periodLabel = `${dateFrom ? fmtDate(dateFrom) : "Awal"} — ${dateTo ? fmtDate(dateTo) : "Sekarang"}`;
    const clsLabel = (t) => {
      const MAP = {
        invoice_counted: "Penerimaan Invoice", invoice_excluded_test: "Invoice TEST",
        invoice_excluded_internal: "Invoice Internal", invoice_excluded_retention: "Klaim Retensi",
        petty_cash_return: "Pengembalian Kas",
      };
      return t.direction === "in" ? (MAP[t.classification] || "—") : "—";
    };
    const rowsHtml = view.rows.map((t) => `
      <tr>
        <td>${esc(t.date ? fmtDate(t.date) : "—")}</td>
        <td>${esc(t.description || "-")}</td>
        <td>${esc(t.project_name || "-")}</td>
        <td>${esc(clsLabel(t))}</td>
        <td class="num">${t.direction === "in" ? esc(formatIDR(t.amount)) : ""}</td>
        <td class="num">${t.direction === "out" ? esc(formatIDR(t.amount)) : ""}</td>
        <td class="num">${esc(formatIDR(t.running_balance))}</td>
      </tr>`).join("");
    const html = `<!DOCTYPE html><html lang="id"><head><meta charset="utf-8">
<title>Buku Bank — ${esc(acc.bank_name || "")}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #0B1D3A; padding: 28px 32px; }
  .company { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #0B1D3A; padding-bottom: 12px; margin-bottom: 18px; }
  .company .name { font-size: 17px; font-weight: 800; letter-spacing: -0.3px; }
  .company .tag { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1.5px; }
  .doc-info { text-align: right; font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
  .doc-info strong { display: block; color: #0B1D3A; font-size: 14px; margin-bottom: 2px; text-transform: none; }
  .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px 16px; margin-bottom: 16px; padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; }
  .meta .k { font-size: 8.5px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
  .meta .v { font-size: 11px; font-weight: 600; margin-top: 1px; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 14px; }
  table th { text-align: left; background: #0B1D3A; color: #fff; padding: 6px 10px; font-size: 9.5px; text-transform: uppercase; letter-spacing: 1px; }
  table td { padding: 5px 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; }
  table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  tr.opening td { background: #f1f5f9; font-style: italic; font-weight: 600; }
  tr.total td { font-weight: 700; border-top: 2px solid #0B1D3A; background: #f1f5f9; }
  .notice { margin-top: 16px; padding: 8px 10px; background: #fef3c7; border-left: 3px solid #f59e0b; font-size: 9px; color: #78350f; line-height: 1.5; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
</style></head><body>
  <div class="company">
    <div><div class="name">PT. Eka Nusa Sinergi</div><div class="tag">Register Buku Bank</div></div>
    <div class="doc-info"><strong>${esc(acc.bank_name || "")}</strong>No. Rek: ${esc(acc.account_no || "-")}</div>
  </div>
  <div class="meta">
    <div><div class="k">Periode</div><div class="v">${esc(periodLabel)}</div></div>
    <div><div class="k">Saldo Awal Periode</div><div class="v">${esc(formatIDR(view.openingPeriod))}</div></div>
    <div><div class="k">Jumlah Mutasi</div><div class="v">${view.count} transaksi</div></div>
    <div><div class="k">Saldo Akhir Periode</div><div class="v">${esc(formatIDR(view.closing))}</div></div>
  </div>
  <table>
    <thead><tr><th>Tanggal</th><th>Keterangan</th><th>Proyek</th><th>Klasifikasi</th><th style="text-align:right">Masuk</th><th style="text-align:right">Keluar</th><th style="text-align:right">Saldo</th></tr></thead>
    <tbody>
      <tr class="opening"><td>—</td><td>Saldo Awal Periode</td><td>—</td><td>—</td><td class="num"></td><td class="num"></td><td class="num">${esc(formatIDR(view.openingPeriod))}</td></tr>
      ${rowsHtml}
      <tr class="total"><td colspan="4">TOTAL</td><td class="num">${esc(formatIDR(view.totalIn))}</td><td class="num">${esc(formatIDR(view.totalOut))}</td><td class="num">${esc(formatIDR(view.closing))}</td></tr>
    </tbody>
  </table>
  <div class="notice">Saldo dihitung dari transaksi masuk dan keluar yang dicatat di sistem, bukan saldo aktual rekening bank. Dicetak: ${esc(new Date().toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }))} WIB.</div>
  <script>window.onload = function() { setTimeout(function() { window.print(); }, 400); };</script>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank", "width=1100,height=900");
    if (!w) { URL.revokeObjectURL(url); toast.error("Popup diblokir browser. Izinkan popup untuk mencetak."); return; }
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  if (loadingAcc) return <div className="p-8 text-center text-xs italic text-slate-400" data-testid="bbreg-loading">Memuat rekening…</div>;
  if (accounts.length === 0) return (
    <div className="bg-white border border-slate-200 p-10 text-center" data-testid="bbreg-empty">
      <Landmark size={28} className="mx-auto text-slate-300 mb-3" />
      <div className="text-sm font-semibold text-[#0B1D3A]">Buku Bank</div>
      <div className="text-xs text-slate-500 mt-1">Belum ada rekening perusahaan. Tambahkan lewat Settings → Keuangan → Bank Perusahaan.</div>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200" data-testid="bbreg-tab">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 flex-wrap">
        <BookOpen size={15} className="text-[#0B1D3A]" />
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">Register Buku Bank</div>
        <span className="text-[10px] text-slate-400 italic">Saldo berjalan per rekening · filter periode · cetak PDF</span>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} data-testid="bbreg-account"
            className="border border-slate-300 px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.bank_name} · {a.account_no}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="bbreg-from"
            className="border border-slate-300 px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
          <span className="text-[10px] text-slate-400">s/d</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="bbreg-to"
            className="border border-slate-300 px-2 py-1 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
          <select value={regProject} onChange={(e) => setRegProject(e.target.value)} data-testid="bbreg-filter-project"
            title="Filter per proyek"
            className="border border-slate-300 px-2 py-1 text-[11px] bg-white max-w-[200px] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
            <option value="">Semua Proyek</option>
            {projectOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input type="text" value={regSearch} onChange={(e) => setRegSearch(e.target.value)} placeholder="Cari transaksi…" data-testid="bbreg-search"
            title="Cari di keterangan, proyek, referensi, klasifikasi"
            className="border border-slate-300 px-2 py-1 text-[11px] bg-white w-40 focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]" />
          {(dateFrom || dateTo || regProject || regSearch) && (
            <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); setRegProject(""); setRegSearch(""); }} data-testid="bbreg-clear"
              className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 hover:text-[#0B1D3A]">Reset</button>
          )}
          <button type="button" onClick={exportExcel} disabled={exporting || !view || view.count === 0} data-testid="bbreg-export-excel"
            title="Export Excel — mengikuti rentang tanggal aktif"
            className="inline-flex items-center gap-1 px-2.5 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 text-[10px] uppercase tracking-wider font-semibold transition-colors">
            <FileDown size={11} /> {exporting ? "Menyiapkan…" : "Export Excel"}
          </button>
          <button type="button" onClick={printPdf} disabled={!view || view.count === 0} data-testid="bbreg-print"
            className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0B1D3A] hover:bg-[#12284d] disabled:opacity-40 text-white text-[10px] uppercase tracking-wider font-semibold transition-colors">
            <Printer size={11} /> Cetak PDF
          </button>
        </div>
      </div>

      {error ? (
        <div className="p-6 text-center text-[11px] text-rose-700 bg-rose-50" data-testid="bbreg-error">{error}</div>
      ) : loading || !view ? (
        <div className="p-8 text-center text-xs italic text-slate-400" data-testid="bbreg-loading-ledger">Memuat register…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 py-3 border-b border-slate-200 bg-slate-50/60" data-testid="bbreg-summary">
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Saldo Awal Periode</div>
              <div className="font-display text-base font-bold tabular-nums text-slate-700" data-testid="bbreg-opening">{formatIDR(view.openingPeriod)}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Mutasi Masuk</div>
              <div className="font-display text-base font-bold tabular-nums text-emerald-700" data-testid="bbreg-total-in">{formatIDR(view.totalIn)}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Mutasi Keluar</div>
              <div className="font-display text-base font-bold tabular-nums text-rose-700" data-testid="bbreg-total-out">{formatIDR(view.totalOut)}</div>
            </div>
            <div className="bg-white border border-slate-200 px-3 py-2">
              <div className="overline text-[9px] text-slate-500">Saldo Akhir Periode</div>
              <div className={`font-display text-base font-bold tabular-nums ${view.closing < 0 ? "text-rose-700" : "text-[#0B1D3A]"}`} data-testid="bbreg-closing">{formatIDR(view.closing)}</div>
            </div>
          </div>

          {view.rows.length === 0 ? (
            <div className="p-8 text-center text-[11px] text-slate-400" data-testid="bbreg-no-rows">
              {(regProject || regSearch) ? "Tidak ada mutasi yang cocok dengan filter proyek/pencarian terpilih." : "Tidak ada mutasi pada periode ini."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="bbreg-table">
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-600">
                  <tr>
                    {/* 2026-10-01 (user request): header sticky — judul kolom tetap
                        terlihat saat tabel panjang di-scroll (konsisten dgn Report Petty Cash). */}
                    <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-left px-3 py-2">Tanggal</th>
                    <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-left px-3 py-2">Keterangan</th>
                    <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-left px-3 py-2">Proyek</th>
                    <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-right px-3 py-2">Masuk</th>
                    <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-right px-3 py-2">Keluar</th>
                    <th className="sticky top-0 z-10 bg-slate-50 shadow-[inset_0_-1px_0_#e2e8f0] text-right px-3 py-2">Saldo Berjalan</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-200 bg-slate-50/70" data-testid="bbreg-row-opening">
                    <td className="px-3 py-1.5 tabular-nums text-slate-400">—</td>
                    <td className="px-3 py-1.5 font-semibold text-slate-700 italic">Saldo Awal Periode</td>
                    <td className="px-3 py-1.5 text-slate-400">—</td>
                    <td className="px-3 py-1.5"></td>
                    <td className="px-3 py-1.5"></td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-bold text-[#0B1D3A]">{formatIDR(view.openingPeriod)}</td>
                  </tr>
                  {view.rows.map((t, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`bbreg-row-${i}`}>
                      <td className="px-3 py-1.5 tabular-nums text-slate-600 whitespace-nowrap">{t.date ? fmtDate(t.date) : "—"}</td>
                      <td className="px-3 py-1.5 text-slate-800">{t.description}</td>
                      <td className="px-3 py-1.5 text-slate-500 max-w-[220px] truncate" title={t.project_name}>{t.project_name}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-emerald-700">{t.direction === "in" ? formatIDR(t.amount) : ""}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">{t.direction === "out" ? formatIDR(t.amount) : ""}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${t.running_balance < 0 ? "text-rose-700" : "text-[#0B1D3A]"} tabular-nums`} data-testid={`bbreg-saldo-${i}`}>{formatIDR(t.running_balance)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-[#0B1D3A] bg-slate-100/70" data-testid="bbreg-row-total">
                    <td className="px-3 py-2 font-bold text-[10px] uppercase tracking-wider" colSpan={3}>Total ({view.count} mutasi)</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-700">{formatIDR(view.totalIn)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-rose-700">{formatIDR(view.totalOut)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${view.closing < 0 ? "text-rose-700" : "text-[#0B1D3A]"}`}>{formatIDR(view.closing)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
